// Quick Support (docs/design/QUICK-SUPPORT.md, the HTTP contract in server/support_http.h): the web client's side of
// a relay's ticket broker. DOM-free, so the tests run it in Node:
//   - support codes and links (the same lenient rules as common/src/support.c),
//   - the broker's JSON (tickets, status, helpers) and what a ticket's state means for the UI,
//   - the broker HTTP client: the app proof (GET /api/support/challenge, then one request with
//     "Authorization: MonkRelay <nonce>:<proof>", the proof computed by the WASM core from the relay secret) or the
//     admin page's cookie,
//   - which tickets become "Support: <name>" devices in My devices, and when they are forgotten.
// The broker sends no CORS headers: a page can only use the broker of the relay that serves it (same origin).

import type { DeviceRecord, RelayRecord, SupportTag } from './store';

export const CODE_LEN = 10;
const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
export const NAME_MAX = 64; // bytes of UTF-8 (MONK_SUPPORT_NAME_MAX)
export const MAX_HELPER_KEYS = 8;
/** Defaults and bounds of the expiries in seconds (support.h). */
export const EXPIRY = { DEFAULT_UNUSED: 3600, DEFAULT_SESSION: 86400, MIN_UNUSED: 60, MAX_UNUSED: 604800 } as const;
/** How often the helper's page asks for the tickets: while one is open, and otherwise. */
export const POLL_OPEN_MS = 10_000;
export const POLL_IDLE_MS = 60_000;

// ---- codes and links ---------------------------------------------------------------------------------------------
/** One lenient code character: its canonical form, '' for a separator, null when it is not a code character. */
function codeChar(ch: string): string | null {
  if (ch === '-' || ch === ' ' || ch === '\t' || ch === '.' || ch === '_') return '';
  let c = ch.toUpperCase();
  if (c === 'I' || c === 'L') c = '1';
  if (c === 'O') c = '0';
  return c.length === 1 && CROCKFORD.includes(c) ? c : null;
}

/** A code in any lenient form (case, dashes, spaces, dots, underscores; I/L = 1, O = 0) -> "K7QX2MH9VZ", else null. */
export function normalizeCode(input: string): string | null {
  let out = '';
  for (const ch of input) {
    const c = codeChar(ch);
    if (c === null || (c && out.length >= CODE_LEN)) return null;
    out += c;
  }
  return out.length === CODE_LEN ? out : null;
}

/** "K7QX2-MH9VZ" */
export function displayCode(code: string): string | null {
  const c = normalizeCode(code);
  return c ? `${c.slice(0, 5)}-${c.slice(5)}` : null;
}

/** The code of a landing page path: /s/CODE (a trailing slash is fine). */
export function codeFromPath(pathname: string): string | null {
  const m = /^\/s\/([^/?#]{1,32})\/?$/.exec(pathname);
  if (!m) return null;
  try {
    return normalizeCode(decodeURIComponent(m[1]));
  } catch {
    return null;
  }
}

/** https://HOST/s/CODE for a code in any form ('' when the code is not valid). */
export function supportLink(origin: string, code: string): string {
  const c = normalizeCode(code);
  return c ? `${origin.replace(/\/+$/, '')}/s/${c}` : '';
}

/** monk_support_name_ok: 1..64 bytes of UTF-8, no control characters, not only spaces. */
export function nameOk(s: string): boolean {
  if (!s.trim()) return false;
  if (new TextEncoder().encode(s).length > NAME_MAX) return false;
  // eslint-disable-next-line no-control-regex
  return !/[\u0000-\u001f\u007f-\u009f]/.test(s);
}

/** Cuts a name to NAME_MAX bytes without splitting a character. */
export function clipName(s: string): string {
  let out = '';
  let n = 0;
  const enc = new TextEncoder();
  for (const ch of s.trim()) {
    n += enc.encode(ch).length;
    if (n > NAME_MAX) break;
    out += ch;
  }
  return out;
}

// ---- JSON ----------------------------------------------------------------------------------------------------------
export type TicketState = 'waiting' | 'fetched' | 'claimed' | 'expired' | 'revoked';
const STATES: readonly TicketState[] = ['waiting', 'fetched', 'claimed', 'expired', 'revoked'];

export interface SupportClaim {
  hostPubkey: string; // 64 hex, lower case: the support host's ephemeral key (pin it)
  deviceId: string; // "ABCD-EFGH"
  os: number; // MONK_OS_*: 1 macOS, 2 Windows, 0 unknown
  claimedAt: number; // unix seconds
}

export interface SupportTicket {
  code: string;
  name: string; // who is helped
  helperName: string; // who helps ("" = "your helper")
  helperKeys: string[]; // 64 hex, lower case: the client keys the support host lets in
  fullControl: boolean;
  created: number; // unix seconds, like the expiries
  expiryUnused: number;
  expirySession: number;
  revoked: boolean;
  fetched: boolean;
  claim: SupportClaim | null;
  state: TicketState;
  online: boolean; // the claimed host is registered on the relay right now
  url: string; // https://DOMAIN/s/CODE ("" = unknown)
}

const HEX64 = /^[0-9a-fA-F]{64}$/;
type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);
const isUint = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0;

function parseClaim(v: unknown): SupportClaim | null | undefined {
  if (v === null || v === undefined) return null;
  if (!isObj(v) || typeof v.host_pubkey !== 'string' || !HEX64.test(v.host_pubkey)) return undefined;
  const os = v.os === undefined ? 0 : v.os;
  if (!isUint(os) || os > 2) return undefined;
  return {
    hostPubkey: v.host_pubkey.toLowerCase(),
    deviceId: typeof v.device_id === 'string' ? v.device_id : '',
    os,
    claimedAt: isUint(v.claimed_at) ? v.claimed_at : 0,
  };
}

/** The state at `now` (unix seconds) from the record, as monk_support_ticket_state computes it. */
export function computeState(t: Pick<SupportTicket, 'revoked' | 'claim' | 'fetched' | 'expiryUnused' | 'expirySession'>, now: number): TicketState {
  if (t.revoked) return 'revoked';
  if (now >= t.expirySession || (!t.claim && now >= t.expiryUnused)) return 'expired';
  if (t.claim) return 'claimed';
  return t.fetched ? 'fetched' : 'waiting';
}

/** One ticket of GET /api/support/tickets (list form; the store form works too: its state is computed). */
export function parseTicket(v: unknown, now = Math.floor(Date.now() / 1000)): SupportTicket | null {
  if (!isObj(v)) return null;
  const code = typeof v.code === 'string' ? normalizeCode(v.code) : null;
  if (!code || v.code !== code) return null;
  if (typeof v.name !== 'string' || !v.name) return null;
  if (!Array.isArray(v.helper_keys) || !v.helper_keys.length || v.helper_keys.length > MAX_HELPER_KEYS) return null;
  if (!v.helper_keys.every((k) => typeof k === 'string' && HEX64.test(k))) return null;
  if (!isUint(v.created) || !isUint(v.expiry_unused) || !isUint(v.expiry_session)) return null;
  const claim = parseClaim(v.claim);
  if (claim === undefined) return null;
  const t: SupportTicket = {
    code,
    name: v.name,
    helperName: typeof v.helper_name === 'string' ? v.helper_name : '',
    helperKeys: (v.helper_keys as string[]).map((k) => k.toLowerCase()),
    fullControl: v.full_control === true,
    created: v.created,
    expiryUnused: v.expiry_unused,
    expirySession: v.expiry_session,
    revoked: v.revoked === true,
    fetched: v.fetched === true,
    claim,
    state: 'waiting',
    online: v.online === true,
    url: typeof v.url === 'string' ? v.url : '',
  };
  t.state = typeof v.state === 'string' && (STATES as readonly string[]).includes(v.state) ? v.state as TicketState : computeState(t, now);
  return t;
}

/** {"tickets":[...],"now":N}: tickets that don't parse are left out (a newer relay's record must not hide the rest). */
export function parseTicketList(v: unknown): { tickets: SupportTicket[]; now: number } | null {
  if (!isObj(v) || !Array.isArray(v.tickets)) return null;
  const now = isUint(v.now) ? v.now : Math.floor(Date.now() / 1000);
  const tickets: SupportTicket[] = [];
  for (const x of v.tickets) {
    const t = parseTicket(x, now);
    if (t) tickets.push(t);
  }
  return { tickets, now };
}

export interface SupportStatus {
  state: 'waiting' | 'fetched' | 'claimed';
  name: string;
  helperName: string;
  expiryUnused: number;
  expirySession: number;
}

/** GET /api/support/CODE/status (200). */
export function parseStatus(v: unknown): SupportStatus | null {
  if (!isObj(v) || (v.state !== 'waiting' && v.state !== 'fetched' && v.state !== 'claimed')) return null;
  if (typeof v.name !== 'string' || !isUint(v.expiry_unused) || !isUint(v.expiry_session)) return null;
  return {
    state: v.state,
    name: v.name,
    helperName: typeof v.helper_name === 'string' ? v.helper_name : '',
    expiryUnused: v.expiry_unused,
    expirySession: v.expiry_session,
  };
}

export interface SupportHelper {
  key: string; // 64 hex, lower case
  name: string;
  added: number; // unix seconds
}

export function parseHelpers(v: unknown): SupportHelper[] | null {
  if (!isObj(v) || !Array.isArray(v.helpers)) return null;
  const out: SupportHelper[] = [];
  for (const x of v.helpers) {
    if (!isObj(x) || typeof x.key !== 'string' || !HEX64.test(x.key)) continue;
    out.push({ key: x.key.toLowerCase(), name: typeof x.name === 'string' ? x.name : '', added: isUint(x.added) ? x.added : 0 });
  }
  return out;
}

export interface CreateRequest {
  name: string;
  helperName?: string;
  fullControl: boolean;
  expiryUnusedS?: number; // 0 / absent = the default (1 h)
  expirySessionS?: number;
  helperKeys?: string[]; // absent = every helper registered on the relay
}

/** The POST /api/support/tickets body, or null when the request is not valid. */
export function createBody(r: CreateRequest): string | null {
  if (!nameOk(r.name) || (r.helperName && !nameOk(r.helperName))) return null;
  const o: Obj = { name: r.name };
  if (r.helperName) o.helper_name = r.helperName;
  o.full_control = !!r.fullControl;
  if (r.expiryUnusedS) {
    if (!isUint(r.expiryUnusedS) || r.expiryUnusedS < EXPIRY.MIN_UNUSED || r.expiryUnusedS > EXPIRY.MAX_UNUSED) return null;
    o.expiry_unused_s = r.expiryUnusedS;
  }
  if (r.expirySessionS) {
    if (!isUint(r.expirySessionS)) return null;
    o.expiry_session_s = r.expirySessionS;
  }
  if (r.helperKeys) {
    if (!r.helperKeys.length || r.helperKeys.length > MAX_HELPER_KEYS || !r.helperKeys.every((k) => HEX64.test(k))) return null;
    o.helper_keys = r.helperKeys.map((k) => k.toLowerCase());
  }
  return JSON.stringify(o);
}

// ---- what the UI shows -------------------------------------------------------------------------------------------
/** waiting: nobody opened the app yet; downloaded: the support app fetched its setup; online / offline: it claimed
 *  the ticket (a device to connect to) and is / isn't on the relay now; ended: expired or revoked. */
export type TicketPhase = 'waiting' | 'downloaded' | 'online' | 'offline' | 'ended';

export function ticketPhase(t: SupportTicket): TicketPhase {
  switch (t.state) {
    case 'waiting': return 'waiting';
    case 'fetched': return 'downloaded';
    case 'claimed': return t.online ? 'online' : 'offline';
    default: return 'ended';
  }
}

export function ticketOpen(t: SupportTicket): boolean {
  return t.state === 'waiting' || t.state === 'fetched' || t.state === 'claimed';
}

/** The words for a ticket's state (support links list, admin page). */
export function phaseText(t: SupportTicket): string {
  switch (ticketPhase(t)) {
    case 'waiting': return 'Waiting';
    case 'downloaded': return 'Downloaded';
    case 'online': return 'Online';
    case 'offline': return 'Offline';
    default: return t.state === 'revoked' ? 'Revoked' : 'Expired';
  }
}

/** "1 hour", "15 minutes", "2 days" */
export function durationText(s: number): string {
  const unit = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;
  if (s < 3600) return unit(Math.max(1, Math.round(s / 60)), 'minute');
  if (s < 86400) return unit(Math.round(s / 3600), 'hour');
  return unit(Math.round(s / 86400), 'day');
}

/** Whether `publicKey` (this browser's) may connect to the ticket's support host. */
export function ticketHasKey(t: SupportTicket, publicKey: string): boolean {
  return t.helperKeys.includes(publicKey.toLowerCase());
}

// ---- the broker client -----------------------------------------------------------------------------------------
export interface FetchResponse {
  status: number;
  headers: { get(name: string): string | null };
  text(): Promise<string>;
}
export type FetchFn = (url: string, init: { method: string; headers: Record<string, string>; body?: string; credentials: 'same-origin'; cache: 'no-store' }) => Promise<FetchResponse>;

/** How a request proves it may make links: the relay secret (apps; `header` = WASM core's MonkRelay header for a
 *  nonce) or the admin page's cookie (sent by the browser). */
export type BrokerAuth = { kind: 'app'; header: (nonce: string) => string | null } | { kind: 'admin' };

export type BrokerErrorCode =
  | 'unsupported' // 404 on the API: an older relay without Quick Support
  | 'unreachable' // network error (or a cross-origin broker without CORS)
  | 'unauthorized' | 'gone' | 'not_found' | 'rate_limited' | 'wrong_password' | 'admin_disabled' | 'too_many_tickets'
  | 'too_many_helpers' | 'no_helper_keys' | 'bad_request' | 'bad_origin' | 'weak_password' | 'not_persistent' | 'error';

export class BrokerError extends Error {
  constructor(readonly code: BrokerErrorCode, message: string, readonly status = 0, readonly state?: string, readonly retryAfterS?: number) {
    super(message);
  }
}

/** A sentence for the UI. */
export function brokerMessage(e: unknown): string {
  if (!(e instanceof BrokerError)) return String((e as Error)?.message ?? e);
  switch (e.code) {
    case 'unsupported': return "This relay server doesn't offer Quick Support yet (it needs an update).";
    case 'unreachable': return "Can't reach the support server. Check the connection and try again.";
    case 'unauthorized': return 'The relay server did not accept this browser (check its relay link).';
    case 'rate_limited': return `Too many requests: wait ${e.retryAfterS && e.retryAfterS > 60 ? 'a few minutes' : 'a moment'} and try again.`;
    case 'too_many_tickets': return 'Too many open support links on this server: revoke one first.';
    case 'too_many_helpers': return 'This server already has 8 helper devices: remove one on its admin page first.';
    case 'wrong_password': return 'Wrong password.';
    case 'admin_disabled': return 'The admin page is turned off on this server.';
    case 'weak_password': return 'Use at least 10 characters for the new password.';
    case 'not_persistent': return "This server can't keep a new password (it has no data folder): change MONK_SUPPORT_ADMIN_HASH instead.";
    default: return e.message || 'Something went wrong.';
  }
}

const HTTP_CODES: Partial<Record<string, BrokerErrorCode>> = {
  unauthorized: 'unauthorized', not_logged_in: 'unauthorized', gone: 'gone', not_found: 'not_found', rate_limited: 'rate_limited',
  wrong_password: 'wrong_password', admin_disabled: 'admin_disabled', too_many_tickets: 'too_many_tickets',
  too_many_helpers: 'too_many_helpers', no_helper_keys: 'no_helper_keys', bad_request: 'bad_request', bad_expiry: 'bad_request',
  bad_origin: 'bad_origin', weak_password: 'weak_password', not_persistent: 'not_persistent',
};

/** Wrong passwords tried since the last successful login (the relay counts them; a login sees them once). */
export interface FailedLogins {
  count: number;
  lastAt: number; // unix seconds, 0 = none
  lastIp: string;
}

export function parseFailedLogins(j: unknown): FailedLogins {
  const o = isObj(j) ? j : {};
  return {
    count: isUint(o.failed) ? o.failed : 0,
    lastAt: isUint(o.last_failed_at) ? o.last_failed_at : 0,
    lastIp: typeof o.last_failed_ip === 'string' ? o.last_failed_ip.slice(0, 64) : '',
  };
}

/** "3 wrong passwords were tried since your last login, the latest from 203.0.113.7 (2 hours ago)." ("" = none) */
export function failedLoginsText(f: FailedLogins, ago: (unixS: number) => string): string {
  if (!f.count) return '';
  const what = f.count === 1 ? 'A wrong password was tried' : `${f.count} wrong passwords were tried`;
  const from = f.lastIp ? `, the latest from ${f.lastIp}` : '';
  return `${what} since your last login${from}${f.lastAt ? ` (${ago(f.lastAt)})` : ''}.`;
}

export class BrokerClient {
  /** base: the broker's origin ("https://relay.example.com"; "" = this page's origin). */
  constructor(readonly base: string, private readonly auth: BrokerAuth | null, private readonly fetchFn: FetchFn = (u, i) => fetch(u, i)) {}

  private async raw(method: string, path: string, body?: string, headers: Record<string, string> = {}): Promise<{ status: number; json: unknown; retryAfter?: number }> {
    const h: Record<string, string> = { Accept: 'application/json', ...headers };
    if (body !== undefined) h['Content-Type'] = 'application/json';
    let res: FetchResponse;
    try {
      res = await this.fetchFn(`${this.base}/api/support${path}`, { method, headers: h, body, credentials: 'same-origin', cache: 'no-store' });
    } catch (e) {
      throw new BrokerError('unreachable', String((e as Error)?.message ?? e));
    }
    let json: unknown = null;
    try {
      const t = await res.text();
      json = t ? JSON.parse(t) : null;
    } catch {
      json = null;
    }
    const ra = Number(res.headers.get('Retry-After'));
    return { status: res.status, json, retryAfter: Number.isFinite(ra) && ra > 0 ? ra : undefined };
  }

  private fail(r: { status: number; json: unknown; retryAfter?: number }): never {
    const j = isObj(r.json) ? r.json : {};
    const err = typeof j.error === 'string' ? j.error : '';
    const msg = typeof j.message === 'string' ? j.message : `HTTP ${r.status}`;
    // A 404 without the broker's JSON: the path is not served at all (an older relay, or no Quick Support there).
    if (r.status === 404 && !err) throw new BrokerError('unsupported', msg, 404);
    if (r.status === 429) throw new BrokerError('rate_limited', msg, 429, undefined, r.retryAfter);
    const code = HTTP_CODES[err] ?? (r.status === 401 ? 'unauthorized' : r.status === 410 ? 'gone' : 'error');
    throw new BrokerError(code, msg, r.status, typeof j.state === 'string' ? j.state : undefined, r.retryAfter);
  }

  /** One request that needs the app proof or the admin cookie. */
  private async authed(method: string, path: string, body?: string): Promise<unknown> {
    const headers: Record<string, string> = {};
    if (this.auth?.kind === 'app') headers.Authorization = await this.proofHeader();
    const r = await this.raw(method, path, body, headers);
    if (r.status < 200 || r.status > 299) this.fail(r);
    return r.json;
  }

  /** A fresh challenge, answered: nonces are single use, so every authorized request gets its own. */
  private async proofHeader(): Promise<string> {
    if (this.auth?.kind !== 'app') throw new BrokerError('unauthorized', 'no relay secret');
    const r = await this.raw('GET', '/challenge');
    if (r.status !== 200) this.fail(r);
    const nonce = isObj(r.json) && typeof r.json.nonce === 'string' ? r.json.nonce : '';
    if (!/^[0-9a-f]{32}$/i.test(nonce)) throw new BrokerError(r.json === null ? 'unsupported' : 'error', 'bad challenge from the relay');
    const hdr = this.auth.header(nonce.toLowerCase());
    if (!hdr) throw new BrokerError('error', 'could not compute the relay proof');
    return hdr;
  }

  async tickets(): Promise<{ tickets: SupportTicket[]; now: number }> {
    const j = await this.authed('GET', '/tickets');
    const l = parseTicketList(j);
    if (!l) throw new BrokerError(j === null ? 'unsupported' : 'error', 'bad ticket list from the relay');
    return l;
  }

  async create(req: CreateRequest): Promise<SupportTicket> {
    const body = createBody(req);
    if (!body) throw new BrokerError('bad_request', 'Enter a name (up to 64 characters).');
    const t = parseTicket(await this.authed('POST', '/tickets', body));
    if (!t) throw new BrokerError('error', 'bad ticket from the relay');
    return t;
  }

  async revoke(code: string): Promise<void> {
    const c = normalizeCode(code);
    if (!c) throw new BrokerError('bad_request', 'not a support code');
    try {
      await this.authed('DELETE', `/tickets/${c}`);
    } catch (e) {
      if (e instanceof BrokerError && e.code === 'not_found') return; // gone already
      throw e;
    }
  }

  async helpers(): Promise<SupportHelper[]> {
    const l = parseHelpers(await this.authed('GET', '/helpers'));
    if (!l) throw new BrokerError('error', 'bad helper list from the relay');
    return l;
  }

  async addHelper(key: string, name: string): Promise<void> {
    if (!HEX64.test(key)) throw new BrokerError('bad_request', 'A helper key is 64 hexadecimal characters.');
    const n = clipName(name) || 'Helper';
    await this.authed('POST', '/helpers', JSON.stringify({ key: key.toLowerCase(), name: nameOk(n) ? n : 'Helper' }));
  }

  async removeHelper(key: string): Promise<void> {
    await this.authed('DELETE', `/helpers/${key.toLowerCase()}`);
  }

  /** The landing page's view of a code (no credentials): its status, or why there is none. */
  async status(code: string): Promise<{ kind: 'ok'; status: SupportStatus } | { kind: 'ended'; state: string } | { kind: 'unknown' }> {
    const c = normalizeCode(code);
    if (!c) return { kind: 'unknown' };
    const r = await this.raw('GET', `/${c}/status`);
    if (r.status === 200) {
      const s = parseStatus(r.json);
      if (!s) throw new BrokerError(r.json === null ? 'unsupported' : 'error', 'bad status from the relay');
      return { kind: 'ok', status: s };
    }
    if (r.status === 410) return { kind: 'ended', state: isObj(r.json) && typeof r.json.state === 'string' ? r.json.state : 'expired' };
    if (r.status === 404 && isObj(r.json) && r.json.error === 'not_found') return { kind: 'unknown' };
    this.fail(r);
  }

  // ---- admin page (cookie) ----
  async login(password: string): Promise<number> {
    return (await this.loginInfo(password)).expiresIn;
  }

  /** The login, with the wrong passwords tried since the previous one (this login is the one that sees them). */
  async loginInfo(password: string): Promise<{ expiresIn: number; failed: FailedLogins }> {
    const r = await this.raw('POST', '/admin/login', JSON.stringify({ password }));
    if (r.status !== 200) this.fail(r);
    return { expiresIn: isObj(r.json) && isUint(r.json.expires_in) ? r.json.expires_in : 3600, failed: parseFailedLogins(r.json) };
  }

  /** Changes the admin password (the server keeps it; every other admin session ends). */
  async changePassword(current: string, next: string): Promise<void> {
    const r = await this.raw('POST', '/admin/password', JSON.stringify({ current, new: next }));
    if (r.status !== 200) this.fail(r);
  }

  async logout(): Promise<void> {
    await this.raw('POST', '/admin/logout', '{}').catch(() => undefined);
  }

  /** Seconds left of the admin session, or 0 when not logged in. */
  async session(): Promise<number> {
    return (await this.sessionInfo()).left;
  }

  /** The session's seconds left (0 = not logged in) and the wrong passwords tried since this login. */
  async sessionInfo(): Promise<{ left: number; failed: FailedLogins }> {
    const r = await this.raw('GET', '/admin/session');
    if (r.status === 200) {
      // An older front without the broker answers every path with the web client's page (200, not JSON).
      if (!isObj(r.json) || r.json.ok !== true) throw new BrokerError('unsupported', 'no Quick Support on this server', 200);
      return { left: isUint(r.json.expires_in) ? r.json.expires_in : 1, failed: parseFailedLogins(r.json) };
    }
    if (r.status === 401) return { left: 0, failed: parseFailedLogins(null) };
    this.fail(r);
  }
}

// ---- the helper's relays and devices ---------------------------------------------------------------------------
/** The https origin of a relay's web front (its web address, else https://HOST), or null (an IP without one). */
export function brokerOrigin(r: Pick<RelayRecord, 'host' | 'web'>): string | null {
  if (r.web) {
    try {
      const u = new URL(r.web);
      if (u.protocol === 'wss:') u.protocol = 'https:';
      else if (u.protocol === 'ws:') u.protocol = 'http:';
      if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
      return u.origin;
    } catch {
      return null;
    }
  }
  if (/^[0-9.]+$/.test(r.host) || r.host.includes(':')) return null;
  return `https://${r.host.toLowerCase()}`;
}

/** The relays whose broker this page can use: the ones served from this origin (the broker sends no CORS headers). */
export function brokerRelays(relays: RelayRecord[], pageOrigin: string): RelayRecord[] {
  return relays.filter((r) => brokerOrigin(r) === pageOrigin);
}

/** "Support: Mom" */
export function supportLabel(name: string): string {
  return `Support: ${name}`;
}

/** The My devices record for a claimed ticket (prev: the record kept so far). */
export function supportDevice(t: SupportTicket, relayLink: string, prev: DeviceRecord | undefined, nowMs: number): DeviceRecord | null {
  if (!t.claim) return null;
  const tag: SupportTag = { code: t.code, name: t.name, expires: t.expirySession * 1000 };
  return {
    publicKey: t.claim.hostPubkey,
    deviceId: t.claim.deviceId || prev?.deviceId || '',
    name: prev?.name || supportLabel(t.name),
    customName: prev?.customName ?? supportLabel(t.name),
    hostOs: t.claim.os || prev?.hostOs,
    relay: relayLink,
    addedAt: prev?.addedAt ?? nowMs,
    lastConnected: prev?.lastConnected,
    screenNames: prev?.screenNames,
    support: tag,
  };
}

/**
 * What My devices should do after a round of ticket lists. `lists`: per relay link, the tickets it listed, or null
 * when that relay could not be asked (its support devices are kept). Adopts every claimed, open ticket this browser
 * is a helper on (also the ones made on the admin page or in another app), and forgets support devices whose ticket
 * ended, is no longer listed, or is past its session expiry. Never touches a device without the support tag.
 */
export function supportPlan(devices: DeviceRecord[], lists: Map<string, SupportTicket[] | null>, myKey: string, nowMs: number): { put: DeviceRecord[]; remove: string[]; ended: SupportEnded[] } {
  const put: DeviceRecord[] = [];
  const remove = new Set<string>();
  const ended: SupportEnded[] = [];
  const byKey = new Map(devices.map((d) => [d.publicKey, d]));
  const keep = new Set<string>();
  for (const [link, tickets] of lists) {
    for (const t of tickets ?? []) {
      if (!t.claim || t.state !== 'claimed' || !ticketHasKey(t, myKey)) continue;
      const prev = byKey.get(t.claim.hostPubkey);
      if (prev && !prev.support) continue; // already a paired device of its own: leave it alone
      const d = supportDevice(t, link, prev, nowMs);
      if (!d || d.support!.expires <= nowMs) continue;
      keep.add(d.publicKey);
      if (!prev || !sameDevice(prev, d)) put.push(d);
    }
  }
  for (const d of devices) {
    if (!d.support || keep.has(d.publicKey) || remove.has(d.publicKey)) continue;
    if (d.support.expires <= nowMs) {
      remove.add(d.publicKey);
      ended.push({ publicKey: d.publicKey, name: d.support.name, why: 'expired' });
      continue;
    }
    const listed = d.relay !== undefined ? lists.get(d.relay) : undefined;
    if (listed) { // its relay answered and the ticket is not open there any more
      remove.add(d.publicKey);
      const t = listed.find((x) => x.code === d.support!.code);
      ended.push({ publicKey: d.publicKey, name: d.support.name, why: t?.state === 'revoked' ? 'revoked' : t?.state === 'expired' ? 'expired' : 'gone' });
    }
  }
  return { put, remove: [...remove], ended };
}

// ---- when a support session ends ----------------------------------------------------------------------------------
/** Why a support device left My devices: its link was revoked (the helped side's End, a Revoke), expired, or is no
 *  longer listed by a relay that answered. */
export type SupportEndReason = 'revoked' | 'expired' | 'gone';
export interface SupportEnded {
  publicKey: string;
  name: string; // who was helped ("Mom")
  why: SupportEndReason;
}

/** What a stream to a support device says when its link ended, instead of "Reconnecting…" (the stream closes).
 *  byYou: this browser revoked it (Revoke, End support). */
export function endedText(name: string, why: SupportEndReason, byYou: boolean): string {
  const who = name || 'them';
  if (byYou) return `You ended the support session with ${who}.`;
  switch (why) {
    case 'revoked': return `${name || 'They'} ended the support session.`;
    case 'expired': return `The support session with ${who} has expired.`;
    default: return `The support session with ${who} has ended.`;
  }
}

/** After a session to a support device ended from its side, the links are asked at once and again this much later
 *  (the helped side's End may reach the broker just after its BYE reached us). */
export const HOST_GONE_RECHECK_MS = 3000;

/** The ticket polling runs while My devices is on screen, or while any support device exists (in a visible tab), so
 *  a support device whose link ended goes away within ~10 s, streaming or not. */
export function supportPollWanted(devicesPage: boolean, streaming: boolean, visible: boolean, supportDevices: number): boolean {
  return visible && ((devicesPage && !streaming) || supportDevices > 0);
}

function sameDevice(a: DeviceRecord, b: DeviceRecord): boolean {
  return a.deviceId === b.deviceId && a.name === b.name && a.customName === b.customName && a.hostOs === b.hostOs &&
    a.relay === b.relay && a.support?.code === b.support?.code && a.support?.name === b.support?.name &&
    a.support?.expires === b.support?.expires;
}

/** Polls the tickets: every POLL_OPEN_MS while one is open (or a support device exists: round() says so), else every
 *  POLL_IDLE_MS, only while started. */
export class SupportPoller {
  private timer: unknown = null;
  private running = false;
  private inFlight = false;

  constructor(private readonly opts: {
    round: () => Promise<boolean>; // resolves true while some ticket is open
    setTimer?: (fn: () => void, ms: number) => unknown;
    clearTimer?: (t: unknown) => void;
  }) {}

  get active(): boolean {
    return this.running;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    void this.tick();
  }

  stop(): void {
    this.running = false;
    if (this.timer !== null) (this.opts.clearTimer ?? ((t: unknown) => clearTimeout(t as number)))(this.timer);
    this.timer = null;
  }

  /** Asks again now (a ticket was made or revoked). */
  poke(): void {
    if (!this.running) return;
    if (this.timer !== null) (this.opts.clearTimer ?? ((t: unknown) => clearTimeout(t as number)))(this.timer);
    this.timer = null;
    void this.tick();
  }

  private async tick(): Promise<void> {
    this.timer = null;
    if (!this.running || this.inFlight) return;
    this.inFlight = true;
    let open = false;
    try {
      open = await this.opts.round();
    } catch {
      open = false;
    }
    this.inFlight = false;
    if (!this.running || this.timer !== null) return;
    const set = this.opts.setTimer ?? ((f: () => void, t: number) => setTimeout(f, t));
    this.timer = set(() => void this.tick(), open ? POLL_OPEN_MS : POLL_IDLE_MS);
  }
}

// ---- the landing page --------------------------------------------------------------------------------------------
export type ClientOs = 'windows' | 'mac' | 'ios' | 'android' | 'other';

/** The visitor's system, from User-Agent Client Hints when present, else the user agent (an iPad asking for the
 *  desktop site says "Macintosh" but has a touch screen). */
export function detectOs(ua: string, platformHint?: string, maxTouchPoints = 0): ClientOs {
  const p = (platformHint ?? '').toLowerCase();
  if (p === 'windows') return 'windows';
  if (p === 'macos') return 'mac';
  if (p === 'android') return 'android';
  if (p === 'ios') return 'ios';
  if (/iPhone|iPad|iPod/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  if (/Windows NT|Win64|Win32/.test(ua)) return 'windows';
  if (/Macintosh|Mac OS X/.test(ua)) return maxTouchPoints > 1 ? 'ios' : 'mac';
  return 'other';
}

/** The landing page's download for an OS. */
export function downloadPath(code: string, os: 'windows' | 'mac'): string {
  return `/api/support/${normalizeCode(code) ?? ''}/download/${os}`;
}

/** The file name the download arrives as (what to look for in Downloads). */
export function downloadName(code: string): string {
  return `MonkViewer-Support-${normalizeCode(code) ?? ''}.zip`;
}
